import { FC, useContext, useState } from 'react';
import { AudioPlayer, Icon, Alert, Button } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../../../../Localization';
import InterfaceContext from '../../../../../../Context';
import TranslateWrapper from '../../../../../../Translator';
import { AudioPlayerWithSignedUrl } from '../../AudioPlayerWithSignedUrl';
import { MessageContentProps, DeletedArtifactMessageProps } from '../shared/types';
import { ImageAttachment } from './ImageAttachment';
import { prettyTextMessage, getAudioPath, isImageAttachment, getAttachmentFilename } from '../shared/utils';
import { MAX_TRANSCRIPT_CHARS } from '../shared/constants';

// Reusable copy button component
interface CopyButtonProps {
  copied: boolean;
  onClick: () => void;
}

const CopyButton: FC<CopyButtonProps> = ({ copied, onClick }) => {
  return (
    <Button
      onClick={onClick}
      variant="unstyled"
      size="sm"
      className="absolute top-2 right-2 p-1"
      aria-label={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? (
        <Icon name="CheckIcon" size="sm" className="text-green-600" strokeWidth={1.5} />
      ) : (
        <Icon name="ClipboardDocumentIcon" size="sm" className="text-gray-500" strokeWidth={1.5} />
      )}
    </Button>
  );
};

// Component to display deleted attachments message
const DeletedArtifactMessage: FC<DeletedArtifactMessageProps> = ({ message, deletedMetadata }) => {
  return (
    <Alert className="p-2.5 mx-0 max-w-none">
      <span>
        {message
          .replace('$by', deletedMetadata.by)
          .replace('$on', new Date(+deletedMetadata.on).toLocaleDateString('en-US'))}
      </span>
      {' '}
      <span className='italic'>
        {deletedMetadata.reason}
      </span>
    </Alert>
  );
};

const Transcript: FC<any> = ({ transcript }: { transcript: string | undefined }) => {
  const L = useLocalizedStrings();

  const [isExpanded, setIsExpanded] = useState(false);

  if (!transcript) return null;

  const shouldTruncate = transcript.length > MAX_TRANSCRIPT_CHARS;
  const displayText = isExpanded ? transcript : transcript.slice(0, MAX_TRANSCRIPT_CHARS);

  return (
    <div className="my-1.5 text-sm text-gray-500">
      <div className="font-semibold">{L.applicant.comms.transcription}</div>
      <div className="mt-1">
        "{displayText}{shouldTruncate && !isExpanded && "..."}"
        {shouldTruncate && (
          <>
            {" "}
            <Button
              variant="link"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? L.applicant.comms.show_less : L.applicant.comms.show_more}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export const MessageContent: FC<MessageContentProps> = ({
  message,
  metadata,
  hiddenMessage,
  attachments,
  attachmentTypes,
  signedURLs,
  direction,
  copied,
  onCopyText,
  applicantInfo,
}) => {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);

  // Render call messages
  if (message.kind === 'call') {
    if (hiddenMessage && !metadata.hidden?.attachmentOnly) {
      // If message is hidden (not just attachments), show only the hidden message
      return (
        <div className="bg-gray-50/50 border rounded-xl p-2.5">
          <Alert className="text-sm italic p-2.5 max-w-none">{hiddenMessage}</Alert>
        </div>
      );
    }

    const recordingUrl = metadata.recording_url || metadata.RecordingUrl;

    return (
      <div className="bg-gray-50/50 border rounded-xl p-1 relative">
        <div className="flex flex-col gap-2">
          {recordingUrl ? (
            <AudioPlayer
              item={{
                id: String(message.id),
                src: recordingUrl,
              }}
              className="max-w-none"
            />
          ) : metadata.isInboundVoiceRecordingDisabled ? (
            <div className="text-sm text-gray-500 italic p-1.5">{L.applicant.comms.inbound_voice_recordings_disabled}</div>
          ) : (
            <div className="text-sm text-gray-500 italic p-1.5">{L.applicant.comms.no_recording_available}</div>
          )}
          {message.transcript && (
            <div className="text-sm text-gray-600 italic border-t mx-1.5 relative">
              <Transcript transcript={message.transcript} />
              {/* Copy button for transcript */}
              <CopyButton
                copied={copied}
                onClick={() => onCopyText(message.transcript)}
              />
            </div>
          )}
          {/* Render attachments or hidden message for attachments */}
          {hiddenMessage && metadata.hidden?.attachmentOnly ? (
            <div className="text-sm text-gray-600 italic">
              {hiddenMessage}
            </div>
          ) : attachments.length > 0 && Object.keys(signedURLs).length > 0 && (
            <div className="space-y-3">
              {attachments.map((attachmentPath: string, index: number) => {
                const signedUrls = signedURLs[attachmentPath];
                const attachmentType = attachmentTypes[index] || '';

                if (!signedUrls || signedUrls.length === 0) return null;

                return <>
                  {signedUrls.map(signedUrl => {
                    // Check if it's an image
                    const isImage = isImageAttachment(attachmentType, signedUrl);
                    if (isImage) {
                      return (
                        <ImageAttachment
                          key={signedUrl}
                          src={signedUrl}
                          alt={`Attachment ${index + 1}`}
                          originalUrl={attachmentPath}
                          attachmentType={attachmentType}
                        />
                      );
                    }

                    // For non-image attachments, show a link
                    return (
                      <div key={signedUrl}>
                        <a
                          href={signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          {getAttachmentFilename(attachmentPath)}
                        </a>
                      </div>
                    );
                  })}
                </>
              })}
            </div>
          )}
          {/* Show deleted attachments message if applicable */}
          {metadata.attachmentsDeleted && (
            <DeletedArtifactMessage
              message={L.applicant.comms.attachments_deleted || 'Attachments deleted by $by on $on'}
              deletedMetadata={metadata.attachmentsDeleted}
            />
          )}
        </div>
      </div>
    );
  }

  // Render SMS/MMS/WhatsApp messages
  if (message.kind === 'sms' || message.kind === 'mms' || message.kind === 'whatsapp') {
    // Check for audio in various metadata fields
    const { path: audioPath, needsSignedUrl } = getAudioPath(metadata);
    const hasAudio = !!audioPath;

    // Detect blank WhatsApp messages (only attachments, no text)
    const blankWhatsAppMsg = message.kind === 'whatsapp' &&
      message.message.trim().length === 0 &&
      !(metadata.attachments?.length);

    const hasNonAudioAttachments = attachments.length > 0 && !hasAudio;

    return (
      <>
        {hasAudio && audioPath ? (
          needsSignedUrl ? (
            <AudioPlayerWithSignedUrl audioPath={audioPath} messageId={String(message.id)} direction={direction} />
          ) : (
            <div className="bg-gray-50/50 border rounded-xl p-1">
              <AudioPlayer
                item={{
                  id: String(message.id),
                  src: audioPath,
                }}
              />
            </div>
          )
        ) : (message.message || hasNonAudioAttachments || metadata.attachmentsDeleted) ? (
          <div className="bg-gray-50/50 border text-sm rounded-xl p-2.5 relative">
            <div className="flex flex-col gap-2">
              {message.message && (
                hiddenMessage && !metadata.hidden?.attachmentOnly ? (
                  <div className="text-gray-600 italic">{hiddenMessage}</div>
                ) : (
                  <>
                    <div className="mr-2.5">
                      <TranslateWrapper
                        id={`message-${message.id}`}
                        applicant_lang={applicantInfo?.['language'] || 'en'}
                        desired_lang={context.lang}
                        translateText={message.message || ''}
                        body={
                          message.renderedMessage ? (
                            <div dangerouslySetInnerHTML={{ __html: message.renderedMessage }} />
                          ) : (
                            prettyTextMessage(message.message || '', blankWhatsAppMsg, L)
                          )
                        }
                        useComponentLibraryButton={true}
                      />
                    </div>
                    {/* Copy button for text messages */}
                    {!blankWhatsAppMsg && (
                      <CopyButton
                        copied={copied}
                        onClick={() => onCopyText()}
                      />
                    )}
                  </>
                )
              )}
              {/* Render attachments or hidden message for attachments */}
              {hiddenMessage && metadata.hidden?.attachmentOnly ? (
                <div className="text-gray-600 italic">
                  {hiddenMessage}
                </div>
              ) : !hiddenMessage && attachments.length > 0 && Object.keys(signedURLs).length > 0 && (
                <div className="space-y-3">
                  {attachments.map((attachmentPath: string, index: number) => {
                    const signedUrls = signedURLs[attachmentPath];
                    const attachmentType = attachmentTypes[index] || '';

                    if (!signedUrls || signedUrls.length === 0) return null;

                    // Skip audio attachments (already handled above)
                    if (attachmentType.includes('audio')) return null;

                    return <>
                      {signedUrls.map(signedUrl => {
                        // Check if it's an image
                        const isImage = isImageAttachment(attachmentType, signedUrl);
                        if (isImage) {
                          return (
                            <ImageAttachment
                              key={signedUrl}
                              src={signedUrl}
                              alt={`Attachment ${index + 1}`}
                              originalUrl={attachmentPath}
                              attachmentType={attachmentType}
                            />
                          );
                        }

                        // For non-image attachments, show a link
                        return (
                          <div key={signedUrl}>
                            <a
                              href={signedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              {getAttachmentFilename(attachmentPath)}
                            </a>
                          </div>
                        );
                      })}
                    </>
                  })}
                </div>
              )}
              {/* Show deleted attachments message if applicable */}
              {metadata.attachmentsDeleted && (
                <DeletedArtifactMessage
                  message={L.applicant.comms.attachments_deleted || 'Attachments deleted by $by on $on'}
                  deletedMetadata={metadata.attachmentsDeleted}
                />
              )}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  // Render email messages
  if (message.kind === 'email') {
    return (
      <div className="bg-gray-50/50 border rounded-xl p-2.5 relative">
        <div className="flex flex-col gap-2">
          <div className="text-sm">
            {hiddenMessage && !metadata.hidden?.attachmentOnly ? (
              <div className="text-gray-600 italic">{hiddenMessage}</div>
            ) : (
              <>
                <div className="mr-2.5">
                  <TranslateWrapper
                    id={`message-${message.id}`}
                    applicant_lang={applicantInfo?.['language'] || 'en'}
                    desired_lang={context.lang}
                    translateText={message.message || ''}
                    body={
                      message.renderedMessage ? (
                        <div className="markdown overflow-x-auto whitespace-normal" dangerouslySetInnerHTML={{ __html: message.renderedMessage }} />
                      ) : (
                        prettyTextMessage(message.message || '', false, L)
                      )
                    }
                    useComponentLibraryButton={true}
                  />
                </div>
                {/* Copy button for email messages */}
                {message.message && (
                  <CopyButton
                    copied={copied}
                    onClick={() => onCopyText()}
                  />
                )}
              </>
            )}
          </div>
          {/* Render attachments or hidden message for attachments */}
          {hiddenMessage && metadata.hidden?.attachmentOnly ? (
            <div className="text-gray-600 italic">{hiddenMessage}</div>
          ) : !hiddenMessage && attachments.length > 0 && Object.keys(signedURLs).length > 0 && (
            <div className="space-y-3">
              {attachments.map((attachmentPath: string, index: number) => {
                const signedUrls = signedURLs[attachmentPath];
                const attachmentType = attachmentTypes[index] || '';

                if (!signedUrls || signedUrls.length === 0) return null;

                return <>
                  {signedUrls.map(signedUrl => {
                    // Check if it's an image
                    const isImage = isImageAttachment(attachmentType, signedUrl);
                    if (isImage) {
                      return (
                        <ImageAttachment
                          key={signedUrl}
                          src={signedUrl}
                          alt={`Attachment ${index + 1}`}
                          originalUrl={attachmentPath}
                          attachmentType={attachmentType}
                        />
                      );
                    }

                    // For non-image attachments, show a link
                    return (
                      <div key={signedUrl}>
                        <a
                          href={signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          {getAttachmentFilename(attachmentPath)}
                        </a>
                      </div>
                    );
                  })}
                </>
              })}
            </div>
          )}
          {/* Show deleted attachments message if applicable */}
          {metadata.attachmentsDeleted && (
            <DeletedArtifactMessage
              message={L.applicant.comms.attachments_deleted || 'Attachments deleted by $by on $on'}
              deletedMetadata={metadata.attachmentsDeleted}
            />
          )}
        </div>
      </div>
    );
  }

  return null;
};
